import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, useSidebar } from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { setSidebarView } from '@/redux/slicers/sidebar.slice';
import Logo from '@/assets/svg/logo.svg?raw';
import LogoCollapsed from '@/assets/svg/logoCollapse.svg';
import SidebarNavigation from '@/components/Sidebar/SidebarNavigation';
import UserMenu from '@/components/Header/UserMenu';

interface AppSidebarProps {
  data?: MenuItem[];
}

const normalizeRoute = (route?: string) => {
  if (!route) return '';
  const normalized = route.startsWith('/') ? route : `/${route}`;
  return normalized === '/' ? normalized : normalized.replace(/\/+$/, '');
};

const collectRoutes = (items: MenuItem[] = []): string[] => items.flatMap((item) => [
  item.route || '',
  ...('children' in item ? collectRoutes(item.children ?? []) : []),
]);

export const AppSidebar = ({ data = [] }: AppSidebarProps) => {
  const { fetchProfile } = useAuth();
  const { state, setOpenMobile, isMobile } = useSidebar();
  const { user, token } = useRootSelector((rootState) => rootState.auth);
  const location = useLocation();
  const dispatch = useDispatch();
  const currentView = useRootSelector((rootState) => rootState.sidebar.currentView);
  const setCurrentView = (view: 'MAIN' | 'SETTINGS') => dispatch(setSidebarView(view));
  const isCollapsed = state === 'collapsed';
  const menuItems = Array.isArray(data) ? data : [];

  useEffect(() => {
    if (token && !user) fetchProfile();
  }, [token, user, fetchProfile]);

  const activeRoute = useMemo(() => {
    const pathname = normalizeRoute(location.pathname);
    return collectRoutes(menuItems)
      .filter(Boolean)
      .map(normalizeRoute)
      .filter((route) => pathname === route || pathname.startsWith(`${route}/`))
      .sort((first, second) => second.length - first.length)[0];
  }, [location.pathname, menuItems]);

  const isActive = (route?: string) => {
    const normalizedRoute = normalizeRoute(route);
    if (!normalizedRoute) return false;
  if (normalizedRoute === '/settings' || normalizedRoute.startsWith('/settings/')) {
    return location.pathname === normalizedRoute || location.pathname.startsWith(`${normalizedRoute}/`);
  }
    return normalizedRoute === activeRoute;
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent className="!overflow-hidden !bg-main">
        <SidebarGroup className="min-h-0 flex-1">
          <SidebarGroupContent className="relative flex h-full min-h-0 flex-col">
            <div className="mb-[3rem] flex h-[8rem] shrink-0 items-center justify-center border-b border-primary px-[1.85rem]">
              <Link to="/dashboard" aria-label="Go to dashboard" className="flex items-center justify-center">
                {isCollapsed ? (
                  <img src={LogoCollapsed} alt="NezenX" className="h-[2.6rem] w-[2.6rem]" />
                ) : (
                  <span
                    aria-label="NezenX"
                    className="text-[#1D1D1D] dark:text-white [&>svg]:h-auto [&>svg]:w-[14rem]"
                    dangerouslySetInnerHTML={{ __html: Logo }}
                  />
                )}
              </Link>
            </div>

            <div
              className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-[20px]"
            >
              <SidebarNavigation
                currentView={currentView}
                setCurrentView={setCurrentView}
                isCollapsed={isCollapsed}
                menuItems={menuItems}
                isActive={isActive}
                isMobile={isMobile}
                setOpenMobile={setOpenMobile}
              />
            </div>

            <div className="relative z-30 shrink-0 border-t border-primary bg-main px-[1.85rem] py-[1.2rem]">
              <UserMenu />
            </div>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};
